export interface AccountEquityTotal {
  total: number
  /** Scoped accounts whose equity is in `total`. */
  resolvedCount: number
  scopedCount: number
}

/**
 * Sums account equity across the requested scope. An All Accounts snapshot
 * arrives account-by-account, so an account that is still pending holds the
 * total back (null) — a partial sum would briefly present one account as the
 * portfolio. Accounts in `settledAccountIds` are the exception: they've been
 * given up on (grace elapsed, venue down) and are left out of the sum rather
 * than pinning it, which is what `resolvedCount < scopedCount` reports.
 */
export function sumAccountEquities(
  accountIds: readonly string[],
  equityByAccountId: Readonly<Record<string, number | undefined>>,
  settledAccountIds: ReadonlySet<string> = new Set()
): AccountEquityTotal | null {
  let total = 0
  let resolvedCount = 0
  for (const accountId of accountIds) {
    const equity = equityByAccountId[accountId]
    if (equity !== undefined && Number.isFinite(equity)) {
      total += equity
      resolvedCount += 1
    } else if (!settledAccountIds.has(accountId)) {
      return null
    }
  }

  if (resolvedCount === 0) return null
  return { total, resolvedCount, scopedCount: accountIds.length }
}

/**
 * The headline can use either a live aggregate or the last value of a
 * fully-settled historical chart. This keeps partial live snapshots hidden
 * without making the card wait unnecessarily for every WebSocket account.
 */
export function shouldShowPortfolioValueSkeleton({
  isAccountsLoading,
  scopedAccountCount,
  hasLiveEquity,
  hasHistoricalEquity,
}: {
  isAccountsLoading: boolean
  scopedAccountCount: number
  hasLiveEquity: boolean
  hasHistoricalEquity: boolean
}): boolean {
  if (isAccountsLoading) return true
  if (scopedAccountCount === 0) return false
  return !hasLiveEquity && !hasHistoricalEquity
}
